import {
  RANKS,
  SUITS,
  type Card,
  type CardId,
  type MeldContext,
  type Rank,
  type RuleConfig,
  type Suit,
} from './types.js';

const RANK_INDEX = new Map<Rank, number>(RANKS.map((r, i) => [r, i]));

export function cardId(rank: Rank, suit: Suit, copy: number): CardId {
  return `${rank}${suit}:${copy}`;
}

export function jokerId(copy: number): CardId {
  return `JK:${copy}`;
}

/**
 * Whether two ids are the same card to look at — same rank and suit, or both
 * printed jokers — regardless of which deck they came from.
 *
 * Copy indices exist so the engine can tell two physical cards apart. Players
 * cannot: the second 7♠ is indistinguishable from the first across the table,
 * so any rule phrased in terms of what the table can *see* has to compare
 * faces, not identities.
 */
export function sameFace(a: CardId, b: CardId): boolean {
  return a.split(':')[0] === b.split(':')[0];
}

/**
 * Card identity is fully encoded in the id, so a card can be rehydrated from
 * the wire without a lookup table. Ids are the only thing that crosses the
 * network; `Card` objects are a local convenience.
 */
export function parseCard(id: CardId): Card {
  const [face = '', copyRaw = '0'] = id.split(':');
  const copy = Number.parseInt(copyRaw, 10);
  if (face === 'JK') {
    return { id, suit: null, rank: null, copy, printedJoker: true };
  }
  const suit = face.slice(-1) as Suit;
  const rank = face.slice(0, -1) as Rank;
  if (!SUITS.includes(suit) || !RANK_INDEX.has(rank)) {
    throw new Error(`Not a card id: ${id}`);
  }
  return { id, suit, rank, copy, printedJoker: false };
}

const cardCache = new Map<CardId, Card>();

/** Memoised `parseCard` — the solver parses the same ids many thousands of times. */
export function card(id: CardId): Card {
  let c = cardCache.get(id);
  if (c === undefined) {
    c = parseCard(id);
    cardCache.set(id, c);
  }
  return c;
}

export function buildDeck(config: RuleConfig): CardId[] {
  const deck: CardId[] = [];
  for (let d = 0; d < config.deckCount; d++) {
    for (const suit of SUITS) {
      for (const rank of RANKS) deck.push(cardId(rank, suit, d));
    }
  }
  for (let j = 0; j < config.printedJokers; j++) deck.push(jokerId(j));
  return deck;
}

/** Point value of a card when it is left ungrouped (§3.6). */
export function cardValue(id: CardId, config: RuleConfig): number {
  const c = card(id);
  if (c.printedJoker) return config.scoring.joker;
  switch (c.rank) {
    case 'A':
    case 'J':
    case 'Q':
    case 'K':
      return config.scoring.highCard;
    default:
      return Number.parseInt(c.rank as string, 10);
  }
}

/**
 * A wild card counts 0 when ungrouped, exactly as a printed joker does —
 * otherwise holding the wild rank would be a penalty rather than an asset.
 */
export function deadwoodValue(id: CardId, ctx: MeldContext): number {
  if (isWild(id, ctx)) return ctx.config.scoring.joker;
  return cardValue(id, ctx.config);
}

export function isWild(id: CardId, ctx: MeldContext): boolean {
  const c = card(id);
  if (c.printedJoker) return true;
  return ctx.wildRank !== null && c.rank === ctx.wildRank;
}

/**
 * Sequence position of a rank. Ace is dual-valued (low 1, high 14) and is
 * resolved against the candidate window, which is how round-the-corner is
 * excluded by construction rather than by a special case.
 */
export function rankValues(rank: Rank, config: RuleConfig): number[] {
  if (rank === 'A') {
    const out: number[] = [];
    if (config.aceLow) out.push(1);
    if (config.aceHigh) out.push(14);
    return out;
  }
  const idx = RANK_INDEX.get(rank);
  if (idx === undefined) throw new Error(`Unknown rank ${rank}`);
  return [idx + 1];
}

export function suitIndex(suit: Suit): number {
  return SUITS.indexOf(suit);
}

/** The rank made wild by the cut card (§3.1). */
export function wildRankFromCut(cutCard: CardId, config: RuleConfig): Rank {
  const c = card(cutCard);
  return c.printedJoker ? config.wildOnPrintedJokerCut : (c.rank as Rank);
}

export function handValue(ids: readonly CardId[], ctx: MeldContext): number {
  let total = 0;
  for (const id of ids) total += deadwoodValue(id, ctx);
  return total;
}

export function sortForDisplay(ids: readonly CardId[], ctx: MeldContext): CardId[] {
  return [...ids].sort((a, b) => {
    const ca = card(a);
    const cb = card(b);
    const wa = isWild(a, ctx) ? 1 : 0;
    const wb = isWild(b, ctx) ? 1 : 0;
    if (wa !== wb) return wa - wb;
    if (ca.printedJoker !== cb.printedJoker) return ca.printedJoker ? 1 : -1;
    const sa = ca.suit ? suitIndex(ca.suit) : 9;
    const sb = cb.suit ? suitIndex(cb.suit) : 9;
    if (sa !== sb) return sa - sb;
    const ra = ca.rank ? (RANK_INDEX.get(ca.rank) ?? 99) : 99;
    const rb = cb.rank ? (RANK_INDEX.get(cb.rank) ?? 99) : 99;
    if (ra !== rb) return ra - rb;
    return ca.copy - cb.copy;
  });
}
